import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import {
  ActivityField,
  ActivitySearchParams,
} from '../models/activity-search.model'
import { Activity } from '../models/activity.entity'
import { Observable } from 'rxjs'
import {
  ActivitiesListingData,
  ActivitiesListingPage,
  ActivitiesService,
} from '../../activities'
import { mergeById } from '../../../libs/filter.lib'
import { ActivitiesRepository } from '../activities.repository'
import { ExecutionsService } from '../../executions'

interface ActivitiesListingState {
  filters?: ActivitySearchParams
  data?: Activity[]
  extData?: ActivitiesListingData
  dropdownOptions?: ActivitiesListingData
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
  fields?: ActivityField[]
}

const EXECUTIONS_LISTING_INITIAL_STATE: ActivitiesListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class ActivitiesListingStore {
  constructor(
    private activitiesService: ActivitiesService,
    private activitiesRepository: ActivitiesRepository,
    private executionsService: ExecutionsService,
  ) {}

  private state = signal<ActivitiesListingState>(
    EXECUTIONS_LISTING_INITIAL_STATE,
  )

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly activities = computed(() => this.state().data || [])
  readonly extData = computed(() => this.state().extData)
  readonly dropdownOptions = computed(() => this.state().dropdownOptions)
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly fields = computed(() => this.state().fields)

  // Effects

  async search(
    params?: ActivitySearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.activitiesRepository
        .searchActivities$(
          this.parseStateFilterParams(params),
          this.state().fields ?? [],
          this.state().extData,
          returnAll,
        )
        .subscribe({
          next: (data) => {
            this.setData(data)
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async delete(activity: Activity): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.activitiesRepository
        .alert('ACTIVITY_DELETION', activity)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.activitiesService.delete$(activity._id).subscribe({
              next: () => {
                resolve(true)
                this.activitiesRepository.notify({
                  code: 'ACTIVITY_DELETED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  async searchLookup(event: {
    dataSourceKey: string
    q?: string
    dataKeyField?: string
    dataDisplayField?: string
    offset?: number
    limit?: number
  }): Promise<void> {
    type LookupKey = keyof ActivitiesListingData
    type ArrayElement<T> = T extends (infer U)[] ? U : never
    type LookupValue = ArrayElement<
      NonNullable<ActivitiesListingData[LookupKey]>
    >

    const queryKey = (event.dataDisplayField ?? 'name') + ':ct'
    const offset = event.offset ?? 0
    const limit = event.limit ?? 50
    const query = {
      offset,
      limit,
      ...(event.q ? { [queryKey]: event.q } : {}),
    } as Record<string, unknown>

    const serviceMap: Partial<
      Record<LookupKey, () => Observable<LookupValue[]>>
    > = {
      executions: () => this.executionsService.list$(query),
    }

    const search$ = serviceMap[event.dataSourceKey as LookupKey]
    if (!search$) return

    search$().subscribe((items) => {
      this.state.update((s) => {
        const key = event.dataSourceKey as LookupKey
        const prev = (s.dropdownOptions?.[key] ?? []) as { _id: string }[]
        const incoming = items as { _id: string }[]
        const merged = prev.length > 0 ? mergeById(prev, incoming) : incoming
        return {
          ...s,
          dropdownOptions: {
            ...s.extData,
            [event.dataSourceKey]: merged as LookupValue[],
          },
        }
      })
    })
  }

  resetLookups() {
    this.state.set({
      ...this.state(),
      dropdownOptions: undefined,
      extData: undefined,
    })
  }

  // Reducers

  setData(data: ActivitiesListingPage) {
    const extData = this.extData()
    const newExtData = {
      executions: mergeById(extData?.executions, data.extData?.executions),
    }
    const parsedData = data.data?.map((activity) => {
      const executionId = activity.lastExecutionId ?? activity.lastTaskId
      const execution = newExtData.executions?.find(
        (e) => e._id === executionId,
      )
      return execution ? { ...activity, lastExecution: execution } : activity
    })

    this.state.set({
      ...this.state(),
      data: parsedData,
      totalCount: data.totalCount,
      extData: newExtData,
      dropdownOptions: newExtData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: ActivitySearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: ActivitySearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: ActivitySearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof ActivitySearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setFields(fields?: ActivityField[]) {
    this.state.set({ ...this.state(), fields })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  private parseStateFilterParams(
    filters?: ActivitySearchParams,
  ): ActivitySearchParams {
    let params: ActivitySearchParams = filters || {}
    params = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => key !== undefined && value !== undefined,
      ),
    )

    return params
  }
}
